import React, { useState } from "react";
import { toast } from "sonner";
import { api, API_BASE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Download, FileUp, Loader2, CheckCircle2, AlertTriangle, Upload } from "lucide-react";

const STEPS = ["Download template", "Upload file", "Review & commit"];

export const RoPACsvImport = ({ assessmentId, open, onOpenChange, onCommitted, canCommit }) => {
  const [step, setStep] = useState(0);
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const reset = () => { setStep(0); setFile(null); setResult(null); setBusy(false); };

  const downloadTemplate = async () => {
    try {
      const token = localStorage.getItem("grc_token");
      const res = await fetch(`${API_BASE}/ropa/csv-template`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("template download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "cx0-ropa-template.csv";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Template downloaded");
      setStep(1);
    } catch (e) {
      toast.error("Could not download the template");
    }
  };

  const validate = async () => {
    if (!file) { toast.error("Choose a CSV file first"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/ropa/${assessmentId}/csv/validate`, fd);
      setResult(data);
      setStep(2);
      toast.success(`${data.valid_rows} of ${data.total_rows} rows are valid`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Validation failed");
    } finally {
      setBusy(false);
    }
  };

  const commit = async () => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/ropa/${assessmentId}/csv/commit`, fd);
      toast.success(`${data.created} record(s) created · ${data.skipped} skipped`);
      onCommitted?.(data);
      onOpenChange(false);
      reset();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Commit failed");
    } finally {
      setBusy(false);
    }
  };

  const rowsWithIssues = (result?.rows || []).filter((r) => (r.errors || []).length > 0);

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}>
      <DialogContent className="max-w-3xl" data-testid="ropa-csv-dialog">
        <DialogHeader><DialogTitle>Import RoPA records from CSV</DialogTitle>
          <DialogDescription>Download the template, upload your file, then review row-level validation before committing.</DialogDescription></DialogHeader>

        <ol className="flex items-center gap-2 text-[11px]" data-testid="ropa-csv-stepper">
          {STEPS.map((label, i) => (
            <li key={label} className="flex items-center gap-2">
              <span
                className={`w-5 h-5 rounded-full grid place-items-center font-semibold ${
                  i <= step ? "bg-[#003366] text-white" : "bg-slate-100 text-slate-400"
                }`}
              >
                {i + 1}
              </span>
              <span className={i === step ? "text-[#003366] font-semibold" : "text-slate-400"}>{label}</span>
              {i < STEPS.length - 1 && <span className="w-6 h-px bg-slate-200" />}
            </li>
          ))}
        </ol>

        {step === 0 && (
          <div className="space-y-4 py-2">
            <p className="text-xs text-slate-500 leading-relaxed">
              Start from the template so every column and enum matches the DPDP schema. Multi-value cells are
              pipe separated (<code className="font-mono">Customers|Employees</code>); data elements use
              <code className="font-mono"> PAN:High|Aadhaar:High</code>. Log retention must be at least 12 months.
            </p>
            <Button onClick={downloadTemplate} data-testid="ropa-csv-download-template" className="bg-[#008080] hover:bg-[#1a51db]">
              <Download size={15} className="mr-1.5" /> Download CSV template
            </Button>
            <button
              onClick={() => setStep(1)}
              data-testid="ropa-csv-skip-template"
              className="block text-xs text-slate-400 hover:text-[#008080] underline"
            >
              I already have a file
            </button>
          </div>
        )}

        {step === 1 && (
          <div className="space-y-4 py-2">
            <label
              className="flex flex-col items-center justify-center gap-2 border border-dashed border-slate-300 rounded-xl py-10 cursor-pointer hover:border-[#008080] transition-colors"
              data-testid="ropa-csv-dropzone"
            >
              <FileUp size={22} className="text-slate-400" />
              <span className="text-xs text-slate-500">{file ? file.name : "Choose a .csv file"}</span>
              <input
                type="file" accept=".csv,text/csv" className="hidden"
                data-testid="ropa-csv-file-input"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
              />
            </label>
            <DialogFooter>
              <Button variant="outline" onClick={() => setStep(0)} data-testid="ropa-csv-back">Back</Button>
              <Button onClick={validate} disabled={busy || !file} data-testid="ropa-csv-validate" className="bg-[#008080] hover:bg-[#1a51db]">
                {busy ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : <Upload size={15} className="mr-1.5" />}
                Validate rows
              </Button>
            </DialogFooter>
          </div>
        )}

        {step === 2 && result && (
          <div className="space-y-4 py-2">
            <div className="flex flex-wrap gap-3 text-xs">
              <span className="px-2.5 py-1 rounded-full bg-[#388E3C]/10 text-[#388E3C] font-semibold" data-testid="ropa-csv-valid-count">
                {result.valid_rows} valid
              </span>
              <span className="px-2.5 py-1 rounded-full bg-[#D32F2F]/10 text-[#D32F2F] font-semibold" data-testid="ropa-csv-invalid-count">
                {result.invalid_rows} blocked
              </span>
              {result.new_assets?.length > 0 && (
                <span className="px-2.5 py-1 rounded-full bg-slate-100 text-slate-600">
                  {result.new_assets.length} new asset(s) will be created
                </span>
              )}
              {result.new_parties?.length > 0 && (
                <span className="px-2.5 py-1 rounded-full bg-slate-100 text-slate-600">
                  {result.new_parties.length} new party(ies) will be created
                </span>
              )}
            </div>

            {rowsWithIssues.length === 0 ? (
              <div className="flex items-center gap-2 text-xs text-[#388E3C]" data-testid="ropa-csv-no-issues">
                <CheckCircle2 size={15} /> Every row passed validation.
              </div>
            ) : (
              <div className="max-h-[300px] overflow-auto border border-slate-200 rounded-xl">
                <table className="w-full text-xs" data-testid="ropa-csv-issues-table">
                  <thead className="bg-slate-50 sticky top-0">
                    <tr>
                      {["Row", "Record", "Field", "Issue", "Severity"].map((h) => (
                        <th key={h} className="text-left px-3 py-2 text-[10px] uppercase tracking-wide text-slate-500 font-semibold">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rowsWithIssues.flatMap((r) =>
                      r.errors.map((e, i) => (
                        <tr key={`${r.row_number}-${i}`} className="border-t border-slate-100" data-testid={`ropa-csv-issue-${r.row_number}-${i}`}>
                          <td className="px-3 py-2 font-mono text-slate-500">{r.row_number}</td>
                          <td className="px-3 py-2 text-slate-600">{r.name || "—"}</td>
                          <td className="px-3 py-2 font-mono text-slate-500">{e.field}</td>
                          <td className="px-3 py-2 text-slate-600">{e.error}</td>
                          <td className="px-3 py-2">
                            <span className={`inline-flex items-center gap-1 font-semibold ${
                              e.severity === "error" ? "text-[#D32F2F]" : "text-[#F57C00]"
                            }`}>
                              <AlertTriangle size={12} /> {e.severity}
                            </span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setStep(1)} data-testid="ropa-csv-reupload">Upload a different file</Button>
              {canCommit ? (
                <Button
                  onClick={commit} disabled={busy || result.valid_rows === 0}
                  data-testid="ropa-csv-commit" className="bg-[#388E3C] hover:bg-[#2f7a33]"
                >
                  {busy && <Loader2 size={15} className="mr-1.5 animate-spin" />}
                  Commit {result.valid_rows} valid row(s)
                </Button>
              ) : (
                <span className="text-xs text-slate-400" data-testid="ropa-csv-commit-blocked">
                  Only an administrator can commit an import
                </span>
              )}
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
